import { useMemo } from "react";
import {
  GoingStatusEnum,
  RisePredictiveIndexEnum,
  YesNoEnum,
  RisePredictiveModelEnum,
} from "@/__generated__/types";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";
import { useTranslation } from "react-i18next";
import { CURRENT_USER } from "@/api/queries/currentUser";
import { useQuery } from "@apollo/client";
import { SmartFilter } from "./types";
import { useCounselors } from "@/hooks/useCounselors";
import { subYears } from "date-fns";
import { getCommitBeforeDec31Date } from "./util";

export const useSmartFilters = (): {
  smartFilters: SmartFilter[] | undefined;
  loading: boolean;
} => {
  const { cohortYear: currentCohortYear, loading: cohortYearLoading } =
    useCurrentCohortYear();
  const { data: userData, loading: userLoading } = useQuery(CURRENT_USER);
  const { data: counselors, loading: counselorsLoading } = useCounselors();

  const curCounselorId = counselors?.find(
    (counselor) => counselor.id === userData?.me.id,
  )?.id;

  const { t } = useTranslation();
  const loading = cohortYearLoading || userLoading || counselorsLoading;

  const smartFilters = useMemo(() => {
    if (loading) return undefined;

    const beforeDate = getCommitBeforeDec31Date(new Date());
    const cohortYear = [`${currentCohortYear}`];

    const smartFilter: SmartFilter[] = [
      {
        name: t("smartFilters.names.hotTargets"),
        id: "hotTargets",
        tooltip: t("smartFilters.tooltips.hotTargets"),
        options: [
          {
            label: t("smartFilters.options.highCommitIndexNotCommitted"),
            value: "highCommitIndexNotCommitted",
            filterState: new FilterState({
              riseCommitIndex: [RisePredictiveIndexEnum.High],
              funnelStates: ["acceptedDeclined", "admitted"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.goingButNotCommitted"),
            value: "goingButNotCommitted",
            filterState: new FilterState({
              funnelStates: [
                "inquired",
                "applicationStarted",
                "applicationComplete",
                "denied",
                "waitlisted",
                "deferred",
                "admitted",
                "acceptedDeclined",
              ],
              goingStatus: [GoingStatusEnum.Going],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.medCommitIndexNotCommitted"),
            value: "medCommitIndexNotCommitted",
            filterState: new FilterState({
              riseCommitIndex: [RisePredictiveIndexEnum.Medium],
              funnelStates: ["acceptedDeclined", "admitted"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.highApplyIndexNotApplied"),
            value: "highApplyIndexNotApplied",
            filterState: new FilterState({
              applyIndex: [RisePredictiveIndexEnum.High],
              funnelStates: ["inquired", "applicationStarted"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.highYieldIndexNotApplied"),
            value: "highYieldIndexNotApplied",
            filterState: new FilterState({
              yieldIndex: [RisePredictiveIndexEnum.High],
              funnelStates: ["inquired", "applicationStarted"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.hightYieldIndexNotCommitted"),
            value: "hightYieldIndexNotCommitted",
            filterState: new FilterState({
              yieldIndex: [RisePredictiveIndexEnum.High],
              funnelStates: [
                "inquired",
                "applicationStarted",
                "applicationComplete",
                "denied",
                "waitlisted",
                "deferred",
                "admitted",
                "acceptedDeclined",
              ],
              cohortYear,
            }),
          },
        ],
      },
      {
        name: t("smartFilters.names.commitmentRisk"),
        id: "commitmentRisk",
        subtitle: "Admits who...",
        tooltip: t("smartFilters.tooltips.commitmentRisk"),
        options: [
          {
            label: t("smartFilters.options.admitsNotGoing"),
            value: "admitsNotGoing",
            filterState: new FilterState({
              funnelStates: [
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              goingStatus: [GoingStatusEnum.Notgoing],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.admitsGoingElsewhere"),
            value: "admitsGoingElsewhere",
            filterState: new FilterState({
              funnelStates: [
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              goingElsewhere: YesNoEnum.Yes,
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.admitsCommitScoreDown"),
            value: "admitsCommitScoreDown",
            filterState: new FilterState({
              funnelStates: [
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              downTrendingScores: [RisePredictiveModelEnum.Commit],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.admitsMoreActiveOtherCommunity"),
            value: "admitsMoreActive",
            filterState: new FilterState({
              funnelStates: [
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              moreActiveElsewhere: YesNoEnum.Yes,
              cohortYear,
            }),
          },
        ],
      },
      // {
      //   name: t("smartFilters.names.mostEngaged"),
      //   id: "mostEngaged",
      //   tooltip: t("smartFilters.tooltips.mostEngaged"),
      //   options: [
      //     {
      //       label: t("smartFilters.options.top1"),
      //       value: "top1",
      //       filterState: new FilterState({
      //         engagementPercentile: EngagementPercentileEnum.Top_1Percent,
      //         cohortYear,
      //       }),
      //     },
      //     {
      //       label: t("smartFilters.options.top5"),
      //       value: "top5",
      //       filterState: new FilterState({
      //         engagementPercentile: EngagementPercentileEnum.Top_5Percent,
      //         cohortYear,
      //       }),
      //     },
      //     {
      //       label: t("smartFilters.options.top10"),
      //       value: "top10",
      //       filterState: new FilterState({
      //         engagementPercentile: EngagementPercentileEnum.Top_10Percent,
      //         cohortYear,
      //       }),
      //     },
      //     {
      //       label: t("smartFilters.options.top25"),
      //       value: "top25",
      //       filterState: new FilterState({
      //         engagementPercentile: EngagementPercentileEnum.Top_25Percent,
      //         cohortYear,
      //       }),
      //     },
      //   ],
      // },
      {
        name: t("smartFilters.names.drifting"),
        id: "drifting",
        tooltip: t("smartFilters.tooltips.drifting"),
        options: [
          {
            label: t("smartFilters.options.moreActiveElsewhere"),
            value: "moreActiveElsewhere",
            filterState: new FilterState({
              moreActiveElsewhere: YesNoEnum.Yes,
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.admitsMoreActiveElsewhere"),
            value: "admitsMoreActiveElsewhere",
            filterState: new FilterState({
              moreActiveElsewhere: YesNoEnum.Yes,
              funnelStates: [
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.committedMoreActiveElsewhere"),
            value: "committedMoreActiveElsewhere",
            filterState: new FilterState({
              moreActiveElsewhere: YesNoEnum.Yes,
              funnelStates: ["committed", "commitmentWithdrawn", "enrolled"],
              cohortYear,
            }),
          },
        ],
      },
      {
        name: t("smartFilters.names.meltRisk"),
        id: "meltRisk",
        subtitle: t("smartFilters.subtitles.committedStudentsWho"),
        tooltip: t("smartFilters.tooltips.meltRisk"),
        options: [
          {
            label: t("smartFilters.options.committedNotGoing"),
            value: "committedNotGoing",
            filterState: new FilterState({
              goingStatus: [GoingStatusEnum.Notgoing],
              funnelStates: ["committed", "commitmentWithdrawn", "enrolled"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.committedGoingElsewhere"),
            value: "committedGoingElsewhere",
            filterState: new FilterState({
              goingElsewhere: YesNoEnum.Yes,
              funnelStates: ["committed", "commitmentWithdrawn", "enrolled"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.committedMoreActiveOtherCommunity"),
            value: "committedMoreActiveOtherCommunity",
            filterState: new FilterState({
              moreActiveElsewhere: YesNoEnum.Yes,
              funnelStates: ["committed", "commitmentWithdrawn", "enrolled"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.studentsCommittedBeforeDec31"),
            value: "committedBeforeDec31",
            filterState: new FilterState({
              funnelStates: ["committed", "commitmentWithdrawn", "enrolled"],
              cohortYear,
              commitDate: {
                to: beforeDate,
                from: subYears(beforeDate, 1),
              },
            }),
          },
          {
            label: t("smartFilters.options.committedWithHighMelt"),
            value: "committedWithHighMelt",
            filterState: new FilterState({
              meltIndex: [RisePredictiveIndexEnum.High],
              funnelStates: ["committed", "commitmentWithdrawn", "enrolled"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.committedWithLowYield"),
            value: "committedWithLowYield",
            filterState: new FilterState({
              yieldIndex: [RisePredictiveIndexEnum.Low],
              funnelStates: ["committed", "commitmentWithdrawn", "enrolled"],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.committedWithLowCommit"),
            value: "committedWithCommit",
            filterState: new FilterState({
              riseCommitIndex: [RisePredictiveIndexEnum.Low],
              funnelStates: ["committed", "commitmentWithdrawn", "enrolled"],
              cohortYear,
            }),
          },
        ],
      },
      {
        name: t("smartFilters.names.softApps"),
        subtitle: t("smartFilters.subtitles.applicantsWithA"),
        id: "softApps",
        options: [
          {
            label: t("smartFilters.options.appsLowApply"),
            value: "appsLowApplyIndex",
            filterState: new FilterState({
              applyIndex: [RisePredictiveIndexEnum.Low],
              funnelStates: [
                "applicationComplete",
                "denied",
                "waitlisted",
                "deferred",
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.appsLowCommit"),
            value: "appsLowCommitIndex",
            filterState: new FilterState({
              riseCommitIndex: [RisePredictiveIndexEnum.Low],
              funnelStates: [
                "applicationComplete",
                "denied",
                "waitlisted",
                "deferred",
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.appsHighMelt"),
            value: "appsHighMeltIndex",
            filterState: new FilterState({
              meltIndex: [RisePredictiveIndexEnum.High],
              funnelStates: [
                "applicationComplete",
                "denied",
                "waitlisted",
                "deferred",
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              cohortYear,
            }),
          },
          {
            label: t("smartFilters.options.appsLowYield"),
            value: "appsLowYieldIndex",
            filterState: new FilterState({
              yieldIndex: [RisePredictiveIndexEnum.Low],
              funnelStates: [
                "applicationComplete",
                "denied",
                "waitlisted",
                "deferred",
                "admitted",
                "acceptedDeclined",
                "committed",
                "commitmentWithdrawn",
                "enrolled",
              ],
              cohortYear,
            }),
          },
        ],
      },
    ];

    if (curCounselorId) {
      smartFilter.unshift({
        name: t("smartFilters.names.myStudents"),
        id: "myStudents",
        tooltip: t("smartFilters.tooltips.myStudents"),
        options: [
          {
            label: t("smartFilters.names.myStudents"),
            value: "myStudents",
            filterState: new FilterState({
              assignedCounselors: [curCounselorId],
            }),
          },
        ],
      });
    }
    return smartFilter;
  }, [currentCohortYear, curCounselorId, t, loading]);

  return {
    smartFilters,
    loading,
  };
};
